import { Injectable } from '@angular/core';
import { AdminMessages } from '@app/admin/admin.messages';
import { CreateInterestDto } from '@app/domain/interest/model/create-interest.model';
import { InterestType } from '@app/domain/interest/model/interest-type.model';
import { Interest } from '@app/domain/interest/model/interest.model';
import { UserMinimal } from '@app/models/user/user-minimal.model';
import { InterestAPIService } from '@app/domain/interest/api/interest-api.service';
import { UserAPIService } from '@app/shared/api/user.api.service';
import { Globals } from '@app/shared/globals/globals';
import { NotifyUtils } from '@app/shared/utils/notify.utils';
import { Observable, firstValueFrom } from 'rxjs';
import { SettingsMessages } from '../../settings/locale/settings.messages';
import { InFlightRegistry } from '@app/shared/utils/in-flight-registry';

// Single-slot guard keys for operations that allow only one concurrent run regardless of id.
const SET_INTERESTS_KEY = 'set-interests';
const IMPORT_FILE_KEY = 'import-file';

@Injectable({
  providedIn: 'root'
})
export class InterestsBusinessService {

  // key -> the write currently in flight for that key. A duplicate call while one is in flight is
  // coalesced onto the same promise (see InFlightRegistry) rather than rejected.
  private updating: InFlightRegistry;
  private downloadingCSV: boolean;

  constructor(
    public globals: Globals,
    private interestAPI: InterestAPIService,
    private userAPI: UserAPIService,
    private notifyUtils: NotifyUtils
  ) {
    this.updating = new InFlightRegistry();
    this.downloadingCSV = false;
  }

  create(createDto: CreateInterestDto): Promise<Interest> {
    return firstValueFrom(this.interestAPI.create(createDto))
      .then(res => {
        this.notifyUtils.notify(AdminMessages.INTERESTS.SUCCESS_INTEREST_CREATE);
        return res;
      });
  }

  update(id: number, createDto: CreateInterestDto): Promise<Interest> {
    return this.updating.run(id, () =>
      firstValueFrom(this.interestAPI.update(id, createDto))
        .then(res => {
          this.notifyUtils.notify(AdminMessages.INTERESTS.SUCCESS_INTEREST_UPDATE);
          return res;
        })
    );
  }

  delete(id: number): Promise<Interest> {
    return this.updating.run(id, () =>
      firstValueFrom(this.interestAPI.delete(id))
        .then(res => {
          if (res.approved) {
            this.notifyUtils.notify(AdminMessages.INTERESTS.SUCCESS_DELETE_INTEREST_SINGLE);
          } else {
            this.notifyUtils.notify(AdminMessages.INTERESTS.SUCCESS_PENDING_INTEREST_DECLINED);
          }

          return res;
        })
    );
  }

  deleteMultiple(ids: number[]): Promise<Interest[]> {
    return this.updating.run(ids.join(','), () =>
      firstValueFrom(this.interestAPI.deleteMultiple(ids))
        .then(res => {
          this.notifyUtils.notify(AdminMessages.INTERESTS.SUCCESS_DELETE_INTEREST_MULTIPLE);
          return res;
        })
    );
  }

  approve(id: number): Promise<Interest> {
    return this.updating.run(id, () =>
      firstValueFrom(this.interestAPI.approvePendingInterest(id))
        .then(res => {
          this.notifyUtils.notify(AdminMessages.INTERESTS.SUCCESS_PENDING_INTEREST_ACCEPTED);
          return res;
        })
    );
  }

  importFile(file: File): Promise<Interest[]> {
    return this.updating.run(IMPORT_FILE_KEY, () =>
      firstValueFrom(this.interestAPI.importInterests(file))
        .then(res => {
          this.notifyUtils.notify(AdminMessages.INTERESTS.SUCCESS_IMPORT_INTERESTS);
          return res;
        })
    );
  }

  getInterests(
    ids: number[],
    name: string,
    categories: string[],
    interestType: InterestType[],
    approved: boolean
  ): Observable<Interest[]> {
    return this.interestAPI.get(ids, name, categories, interestType, approved);
  }

  getAllUsersUsingInterest(id: number): Observable<UserMinimal[]> {
    return this.interestAPI.getAllUsersUsingInterest(id);
  }

  setInterests(interests: Interest[]): Promise<Interest[]> {
    return this.updating.run(SET_INTERESTS_KEY, () =>
      firstValueFrom(this.userAPI.updateInterests(interests))
        .then(res => {
          this.notifyUtils.notify(SettingsMessages.SUCCESS_PROFILE_UPDATED);
          this.globals.user.personalInterests = res.personalInterests;
          this.globals.user.professionalInterests = res.professionalInterests;
          return [...res.personalInterests, ...res.professionalInterests];
        })
    );
  }

  downloadCSV(): Promise<Blob> {
    if (this.downloadingCSV) { return; }
    this.downloadingCSV = true;

    firstValueFrom(this.interestAPI.downloadCSV())
      .then(res => {
        const url = window.URL.createObjectURL(res);
        const a = document.createElement('a');
        a.href = url;
        a.download = `frankli-interests-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
      })
      .finally(() => {
        this.downloadingCSV = false;
      });
  }

}
